import {
  fmtDateInput,
  nyDateInputToMs,
  nyStartOfDayMs,
} from '@/lib/datetime';
import type { Instant } from '@/lib/datetime';
import {
  PERFORMANCE_METRIC_IDS,
  PERFORMANCE_SORT_FIELDS,
} from '@/lib/performance-metrics';
import type {
  PerformanceMetricId,
  PerformanceSortDirection as MetricSortDirection,
  PerformanceSortField as MetricSortField,
} from '@/lib/performance-metrics';

/**
 * The Performance page's complete user-controlled state.
 *
 * Dataset (`performance`), direct-only attribution, and currency (`USD`) are
 * intentionally absent. They are report invariants, not URL choices that can
 * split the cache or make two links look equivalent while asking for different
 * data.
 */
export const REPORT_TAB_IDS = [
  'overview',
  'campaign',
  'production',
  'channel',
  'audience',
  'audienceType',
  'creative',
] as const;

export const PERFORMANCE_TREND_METRIC_IDS = PERFORMANCE_METRIC_IDS;

export const PERFORMANCE_CHANNEL_MIX_METRIC_IDS = [
  'gross_spend',
  'attributed_conversions',
  'attributed_revenue',
] as const;

export const PERFORMANCE_SORT_FIELD_IDS = PERFORMANCE_SORT_FIELDS;
export const PERFORMANCE_SORT_DIRECTION_IDS = ['asc', 'desc'] as const;
export const PERFORMANCE_TARGET_IDS = ['meta', 'ttd'] as const;
export const PERFORMANCE_CHANNEL_IDS = ['meta', 'ctv', 'dsp'] as const;
export const PERFORMANCE_AUDIENCE_TYPE_IDS = [
  'direct',
  'lookalike',
  'retargeting',
] as const;

export type PerformanceReportTab = (typeof REPORT_TAB_IDS)[number];
export type PerformanceTrendMetric = PerformanceMetricId;
export type PerformanceChannelMixMetric =
  (typeof PERFORMANCE_CHANNEL_MIX_METRIC_IDS)[number];
export type PerformanceSortField = MetricSortField;
export type PerformanceSortDirection = MetricSortDirection;
export type PerformanceTarget = (typeof PERFORMANCE_TARGET_IDS)[number];
export type PerformanceChannel = (typeof PERFORMANCE_CHANNEL_IDS)[number];
export type PerformanceAudienceType =
  (typeof PERFORMANCE_AUDIENCE_TYPE_IDS)[number];

export interface PerformanceReportSearch {
  tab: PerformanceReportTab;
  /** Inclusive, literal America/New_York calendar days. */
  from: string;
  to: string;
  trendMetric: PerformanceTrendMetric;
  channelMixMetric: PerformanceChannelMixMetric;
  productionId?: string;
  campaignId?: string;
  target?: PerformanceTarget;
  channel?: PerformanceChannel;
  /** Audience is a revisioned identity: both fields are present or neither is. */
  audienceId?: string;
  audienceRevision?: number;
  audienceType?: PerformanceAudienceType;
  creativeId?: string;
  sortField: PerformanceSortField;
  sortDirection: PerformanceSortDirection;
}

export type PerformanceReportSearchPatch = Partial<PerformanceReportSearch>;

/** Canonical scalar search object accepted by TanStack Router. */
export type SerializedPerformanceReportSearch = PerformanceReportSearch;

export const DEFAULT_PERFORMANCE_REPORT_TAB: PerformanceReportTab = 'overview';
export const DEFAULT_PERFORMANCE_TREND_METRIC: PerformanceTrendMetric =
  'gross_spend';
export const DEFAULT_PERFORMANCE_CHANNEL_MIX_METRIC: PerformanceChannelMixMetric =
  'attributed_revenue';
export const DEFAULT_PERFORMANCE_SORT_FIELD: PerformanceSortField =
  'attributed_revenue';
export const DEFAULT_PERFORMANCE_SORT_DIRECTION: PerformanceSortDirection = 'desc';

const DAY_MS = 86_400_000;
const HALF_DAY_MS = DAY_MS / 2;
const DEFAULT_COMPLETED_DAYS = 30;
const MAX_RANGE_DAYS = 366;
const MAX_REVISION = 4_294_967_295;

const SLUG_ID = /^[a-z0-9_-]{1,64}$/;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const PRODUCTION_ID = new RegExp(`^(?:${UUID}|Production/${UUID})$`);

interface DateRange {
  from: string;
  to: string;
}

/** Last 30 fully completed NY calendar days; the current partial day is never included. */
export function defaultPerformanceReportDateRange(now: Instant = Date.now()): DateRange {
  const todayStart = nyStartOfDayMs(now);
  let fromStart = todayStart;
  for (let day = 0; day < DEFAULT_COMPLETED_DAYS; day += 1) {
    // Noon-ish on the preceding NY day is safe across both DST transitions;
    // snapping it back to NY midnight advances by one calendar day, not 24h.
    fromStart = nyStartOfDayMs(fromStart - HALF_DAY_MS);
  }
  return {
    from: fmtDateInput(fromStart),
    to: fmtDateInput(todayStart - 1),
  };
}

/**
 * Total URL validator for TanStack Router's `validateSearch`.
 *
 * Invalid closed-set values use their documented defaults. Invalid entity
 * filters are omitted. A bad date endpoint invalidates the pair and restores
 * the last 30 completed NY days, so no malformed URL can silently widen a
 * report beyond the 366-day read bound.
 */
export function parsePerformanceReportSearch(
  search: object,
  now: Instant = Date.now(),
): PerformanceReportSearch {
  const raw = search as Record<string, unknown>;
  const fallbackRange = defaultPerformanceReportDateRange(now);
  const range = parseDateRange(raw.from, raw.to) ?? fallbackRange;
  const audienceId = parseSlugId(raw.audienceId ?? raw.audience);
  const audienceRevision = parseRevision(raw.audienceRevision ?? raw.revision);
  const hasAudienceIdentity = audienceId !== undefined && audienceRevision !== undefined;

  return {
    tab: parseEnum(raw.tab, REPORT_TAB_IDS, DEFAULT_PERFORMANCE_REPORT_TAB),
    ...range,
    trendMetric: parseEnum(
      raw.trendMetric ?? raw.trend,
      PERFORMANCE_TREND_METRIC_IDS,
      DEFAULT_PERFORMANCE_TREND_METRIC,
    ),
    channelMixMetric: parseEnum(
      raw.channelMixMetric ?? raw.mix,
      PERFORMANCE_CHANNEL_MIX_METRIC_IDS,
      DEFAULT_PERFORMANCE_CHANNEL_MIX_METRIC,
    ),
    productionId: parseProductionId(raw.productionId ?? raw.production),
    campaignId: parseSlugId(raw.campaignId ?? raw.campaign),
    target: parseOptionalEnum(raw.target, PERFORMANCE_TARGET_IDS),
    channel: parseOptionalEnum(raw.channel, PERFORMANCE_CHANNEL_IDS),
    audienceId: hasAudienceIdentity ? audienceId : undefined,
    audienceRevision: hasAudienceIdentity ? audienceRevision : undefined,
    audienceType: parseOptionalEnum(
      raw.audienceType ?? raw.audience_type,
      PERFORMANCE_AUDIENCE_TYPE_IDS,
    ),
    creativeId: parseSlugId(raw.creativeId ?? raw.creative),
    sortField: parseEnum(
      raw.sortField ?? raw.sort,
      PERFORMANCE_SORT_FIELD_IDS,
      DEFAULT_PERFORMANCE_SORT_FIELD,
    ),
    sortDirection: parseEnum(
      raw.sortDirection ?? raw.direction,
      PERFORMANCE_SORT_DIRECTION_IDS,
      DEFAULT_PERFORMANCE_SORT_DIRECTION,
    ),
  };
}

/** Write every behavioral default and both date endpoints for reproducible history/bookmarks. */
export function serializePerformanceReportSearch(
  search: PerformanceReportSearch,
): SerializedPerformanceReportSearch {
  return { ...search };
}

/**
 * Apply one UI action while preserving the rest of the URL state.
 *
 * The current valid range is the update fallback (rather than a moving
 * "today" default), which keeps back/forward navigation deterministic if a
 * date picker briefly emits an incomplete or reversed pair.
 */
export function updatePerformanceReportSearch(
  current: PerformanceReportSearch,
  patch: PerformanceReportSearchPatch,
): PerformanceReportSearch {
  const candidate = { ...current, ...patch };
  const range = parseDateRange(candidate.from, candidate.to) ?? {
    from: current.from,
    to: current.to,
  };
  const audienceChangedWithoutRevision =
    Object.prototype.hasOwnProperty.call(patch, 'audienceId')
    && patch.audienceId !== current.audienceId
    && !Object.prototype.hasOwnProperty.call(patch, 'audienceRevision');
  const audienceId = parseSlugId(candidate.audienceId);
  const audienceRevision = audienceChangedWithoutRevision
    ? undefined
    : parseRevision(candidate.audienceRevision);
  const hasAudienceIdentity = audienceId !== undefined && audienceRevision !== undefined;

  return {
    tab: parseEnum(candidate.tab, REPORT_TAB_IDS, current.tab),
    ...range,
    trendMetric: parseEnum(
      candidate.trendMetric,
      PERFORMANCE_TREND_METRIC_IDS,
      current.trendMetric,
    ),
    channelMixMetric: parseEnum(
      candidate.channelMixMetric,
      PERFORMANCE_CHANNEL_MIX_METRIC_IDS,
      current.channelMixMetric,
    ),
    productionId: parseProductionId(candidate.productionId),
    campaignId: parseSlugId(candidate.campaignId),
    target: parseOptionalEnum(candidate.target, PERFORMANCE_TARGET_IDS),
    channel: parseOptionalEnum(candidate.channel, PERFORMANCE_CHANNEL_IDS),
    audienceId: hasAudienceIdentity ? audienceId : undefined,
    audienceRevision: hasAudienceIdentity ? audienceRevision : undefined,
    audienceType: parseOptionalEnum(
      candidate.audienceType,
      PERFORMANCE_AUDIENCE_TYPE_IDS,
    ),
    creativeId: parseSlugId(candidate.creativeId),
    sortField: parseEnum(
      candidate.sortField,
      PERFORMANCE_SORT_FIELD_IDS,
      current.sortField,
    ),
    sortDirection: parseEnum(
      candidate.sortDirection,
      PERFORMANCE_SORT_DIRECTION_IDS,
      current.sortDirection,
    ),
  };
}

function parseDateRange(from: unknown, to: unknown): DateRange | undefined {
  const parsedFrom = parseCalendarDay(from);
  const parsedTo = parseCalendarDay(to);
  if (!parsedFrom || !parsedTo || parsedFrom.ms > parsedTo.ms) return undefined;
  // NY midnights are 23h/25h apart at DST boundaries. Rounding their elapsed
  // duration yields the calendar-day distance while remaining timezone-safe.
  const inclusiveDays = Math.round((parsedTo.ms - parsedFrom.ms) / DAY_MS) + 1;
  if (inclusiveDays > MAX_RANGE_DAYS) return undefined;
  return { from: parsedFrom.value, to: parsedTo.value };
}

function parseCalendarDay(value: unknown): { value: string; ms: number } | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  const ms = nyDateInputToMs(normalized);
  if (!Number.isFinite(ms) || fmtDateInput(ms) !== normalized) return undefined;
  return { value: normalized, ms };
}

function parseSlugId(value: unknown): string | undefined {
  return typeof value === 'string' && SLUG_ID.test(value) ? value : undefined;
}

function parseProductionId(value: unknown): string | undefined {
  return typeof value === 'string' && PRODUCTION_ID.test(value) ? value : undefined;
}

function parseRevision(value: unknown): number | undefined {
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^[1-9][0-9]*$/.test(value)
      ? Number(value)
      : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= MAX_REVISION
    ? parsed
    : undefined;
}

function parseEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
  fallback: T[number],
): T[number] {
  return typeof value === 'string' && values.includes(value)
    ? (value as T[number])
    : fallback;
}

function parseOptionalEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
): T[number] | undefined {
  return typeof value === 'string' && values.includes(value)
    ? (value as T[number])
    : undefined;
}
